import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getProducts } from "../services/api";
import { useCart } from "../context/CartContext";
import { motion } from "framer-motion";
import { ShoppingCart, Zap } from "lucide-react"; // Importing sleek icons

// Custom Card Component for Reusability and better organization
const ProductCard = ({ product, addToCart, navigate }) => {
  const img =
    product.images?.length > 0
      ? product.images[0].image
      : "/fallback.jpg"; // Keep fallback image

  const handleBuyNow = (e) => {
    e.stopPropagation(); // Prevent card navigation
    addToCart(product);
    navigate("/address");
  };

  const handleAddToCart = (e) => {
    e.stopPropagation(); // Prevent card navigation
    addToCart(product);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ scale: 1.03, boxShadow: "0 10px 25px rgba(255, 95, 149, 0.2)" }}
      onClick={() => navigate(`/product/${product.slug}`)} // Make the whole card navigate
      className="min-w-[170px] flex-shrink-0 md:min-w-0 bg-white rounded-3xl shadow-xl border border-pink-50 p-3.5 cursor-pointer transform transition duration-300 ease-in-out hover:shadow-2xl"
    >
      {/* IMAGE */}
      <div className="w-full h-[180px] rounded-2xl overflow-hidden mb-3">
        <img
          src={img}
          alt={product.name}
          className="w-full h-full object-cover transition duration-500 ease-in-out hover:scale-110" // Sexy hover effect
        />
      </div>

      <div className="flex flex-col gap-1">
        {/* TITLE */}
        <h3 className="text-base font-extrabold text-[#36274D] line-clamp-1">
          {product.name}
        </h3>

        {/* PRICE */}
        <p className="text-xl text-[#FF5F95] font-black">
          ₹{product.price}
        </p>

        {/* BUTTONS CONTAINER */}
        <div className="mt-3 flex gap-2">
          {/* BUY BUTTON - Primary CTA */}
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleBuyNow}
            className="flex-1 flex items-center justify-center bg-gradient-to-r from-[#FF5F95] to-[#FF9A95] text-white text-sm py-2.5 rounded-full font-bold shadow-lg shadow-pink-300/50 transition duration-200 hover:from-[#FF4D83] hover:to-[#FF8883]"
          >
            <Zap className="w-4 h-4 mr-1 fill-white" /> Buy Now
          </motion.button>

          {/* ADD TO CART - Secondary CTA (A bit more subtle) */}
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleAddToCart}
            className="w-10 h-10 flex items-center justify-center bg-pink-100 text-[#FF5F95] rounded-full transition duration-200 hover:bg-pink-200"
            title="Add to Cart"
          >
            <ShoppingCart className="w-5 h-5" />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};


export default function TrendingNow() {
  const [trending, setTrending] = useState([]);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    getProducts().then((res) => {
      // Still take only first 6 products as trending
      setTrending(res.slice(0, 6));
    });
  }, []);

  if (!trending.length) {
    return (
      <section className="mt-12 px-4">
        <p className="text-center text-sm text-gray-400 animate-pulse">
          Loading the hottest trends... 🔥
        </p>
      </section>
    );
  }

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="mt-16 w-full px-4 py-12 bg-[#FEE4EC] rounded-t-3xl border-t border-pink-100" // Elevated background
    >
      
      {/* ✅ Heading */}
      <div className="text-center mb-10">
        <h2 className="text-4xl font-extrabold text-[#A81F4B] relative inline-block before:content-[''] before:absolute before:bottom-0 before:left-0 before:w-full before:h-1 before:bg-[#FF5F95] before:rounded-full">
          ✨ Trending Now
        </h2>
        <p className="text-base text-pink-700 mt-2 font-medium">
          The best of the best, adored by our community.
        </p>
      </div>

      {/* ✅ Card Container: 
        - Mobile: Swipeable list (flex gap-4 overflow-x-auto)
        - Desktop: Responsive grid (md:grid-cols-3 lg:grid-cols-4) 
      */}
      <div className="flex gap-6 overflow-x-auto scrollbar-hide pb-5 md:grid md:grid-cols-2 md:gap-8 lg:grid-cols-3 xl:grid-cols-4 md:overflow-x-visible md:pb-0">
        {trending.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            addToCart={addToCart}
            navigate={navigate}
          />
        ))}
      </div>

      {/* ✅ Footer CTA */}
      <div className="text-center mt-12">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate('/products')}
          className="px-8 py-3 bg-[#FF5F95] text-white font-bold text-lg rounded-full shadow-xl shadow-pink-300/50 transition duration-300 hover:bg-[#FF4D83]"
        >
          View All Products →
        </motion.button>
      </div>
    </motion.section>
  );
}